import { useState, useMemo, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import { apiPost } from '../services/apiClient';
import { DIMENSIONS } from '../constants/dimensions';
import '../styles/DimensionDashboards.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ROWS_PER_PAGE = 25;
const LOW_SAMPLE_THRESHOLD = 10;

function npsToColor(nps, count) {
    if (count < LOW_SAMPLE_THRESHOLD) return 'rgba(148, 163, 184, 0.6)';
    const t = (nps + 100) / 200;
    return `hsl(${t * 120}, 70%, 45%)`;
}

function getNPSClass(nps, count) {
    if (count < LOW_SAMPLE_THRESHOLD) return 'nps-low-sample';
    if (nps >= 50) return 'nps-positive';
    if (nps <= -10) return 'nps-negative';
    return 'nps-neutral';
}

/**
 * Pure presentational component for dimension NPS data.
 * Renders bar chart + sortable table + post drilldown.
 * No data fetching — receives everything via props.
 *
 * Props:
 *   data       - array of { dimension_value, nps, count, promoters, passives, detractors, avg_rating, lower_ci, upper_ci }
 *   dimension  - current dimension key ('location', 'source', 'community')
 *   topicId    - selected topic ID (needed for drilldown post fetch)
 *   emptyMessage - optional custom empty state message
 */
export default function DimensionNPSDisplay({ data = [], dimension = 'location', topicId, emptyMessage, initialSort }) {
    const [sortConfig, setSortConfig] = useState(initialSort || { key: 'count', dir: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [tablePage, setTablePage] = useState(0);
    const [drilldown, setDrilldown] = useState(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);

    const sortedData = useMemo(() => {
        const sorted = [...data];
        sorted.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (typeof aVal === 'string') return sortConfig.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
        });
        return sorted;
    }, [data, sortConfig]);

    const filteredData = useMemo(() => {
        if (!searchTerm) return sortedData;
        const term = searchTerm.toLowerCase();
        return sortedData.filter(r => r.dimension_value.toLowerCase().includes(term));
    }, [sortedData, searchTerm]);

    const pagedData = useMemo(() => {
        const start = tablePage * ROWS_PER_PAGE;
        return filteredData.slice(start, start + ROWS_PER_PAGE);
    }, [filteredData, tablePage]);

    const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
        }));
        setTablePage(0);
    }, []);

    const handleDrilldown = useCallback(async (dimensionValue) => {
        if (drilldown?.value === dimensionValue) {
            setDrilldown(null);
            return;
        }
        setDrilldownLoading(true);
        try {
            const res = await apiPost('topic/dimension/posts', {
                topic_id: topicId,
                dimension,
                dimension_value: dimensionValue,
                limit: 50,
            });
            const result = await res.json();
            setDrilldown({ value: dimensionValue, posts: result.data || [] });
        } catch (err) {
            console.error('[DimensionNPSDisplay] drilldown error:', err);
        } finally {
            setDrilldownLoading(false);
        }
    }, [topicId, dimension, drilldown]);

    const chartData = useMemo(() => {
        if (data.length === 0) return null;
        const top = data.slice(0, 20);
        return {
            labels: top.map(d => d.dimension_value),
            datasets: [{
                label: 'Reviews',
                data: top.map(d => d.count),
                backgroundColor: top.map(d => npsToColor(d.nps, d.count)),
                borderRadius: 4,
            }],
        };
    }, [data]);

    const chartOptions = useMemo(() => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const item = data[ctx.dataIndex];
                        return `Reviews: ${item.count} | NPS: ${item.nps}`;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Number of Reviews' },
                grid: { color: '#f1f5f9' },
            },
            y: {
                grid: { display: false },
                ticks: { font: { size: 11 } },
            },
        },
    }), [data]);

    const chartHeight = Math.max(400, (chartData?.labels?.length || 0) * 32);

    const dimConfig = DIMENSIONS.find(d => d.value === dimension) || DIMENSIONS[0];

    const columns = [
        { key: 'rank', label: '#', align: 'right', sortable: false },
        { key: 'dimension_value', label: dimConfig.itemLabel, align: 'left', sortable: true },
        { key: 'count', label: 'Reviews', align: 'right', sortable: true },
        { key: 'nps', label: 'NPS', align: 'right', sortable: true },
        { key: 'avg_rating', label: 'Avg Rating', align: 'right', sortable: true },
    ];

    if (data.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                {emptyMessage || `No ${dimConfig.label.toLowerCase()} data available for this time period.`}
            </div>
        );
    }

    return (
        <div className="location-dashboards-container">
            {/* Bar chart */}
            {chartData && chartData.datasets[0].data.length > 0 && (
                <div className="location-chart-section">
                    <h3>Top {Math.min(20, data.length)} {dimConfig.plural} by Review Volume</h3>
                    <div className="location-chart-container" style={{ height: chartHeight }}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                    <div className="location-chart-legend">
                        <span className="legend-item"><span className="legend-swatch" style={{ background: 'hsl(120, 70%, 45%)' }}></span> High NPS</span>
                        <span className="legend-item"><span className="legend-swatch" style={{ background: 'hsl(60, 70%, 45%)' }}></span> Neutral</span>
                        <span className="legend-item"><span className="legend-swatch" style={{ background: 'hsl(0, 70%, 45%)' }}></span> Low NPS</span>
                        <span className="legend-item"><span className="legend-swatch" style={{ background: 'rgba(148, 163, 184, 0.6)' }}></span> Low sample (&lt;{LOW_SAMPLE_THRESHOLD})</span>
                    </div>
                </div>
            )}

            {/* Table */}
            {sortedData.length > 0 && (<>
                <div className="location-table-search">
                    <input
                        type="text"
                        placeholder={`Search ${dimConfig.plural}...`}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setTablePage(0); }}
                    />
                    {searchTerm && (
                        <span className="location-search-count">
                            {filteredData.length} of {sortedData.length}
                        </span>
                    )}
                </div>
                <div className="location-table-wrapper">
                    <table className="location-table">
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={`${col.align === 'right' ? 'text-right' : ''} ${col.sortable && sortConfig.key === col.key ? `sorted-${sortConfig.dir}` : ''}`}
                                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                                        style={col.sortable ? { cursor: 'pointer' } : undefined}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pagedData.map((row, idx) => (
                                <tr
                                    key={row.dimension_value}
                                    className={drilldown?.value === row.dimension_value ? 'selected' : ''}
                                    onClick={() => handleDrilldown(row.dimension_value)}
                                >
                                    <td className="text-right">{tablePage * ROWS_PER_PAGE + idx + 1}</td>
                                    <td>{row.dimension_value}</td>
                                    <td className="text-right">{row.count}</td>
                                    <td className={`text-right ${getNPSClass(row.nps, row.count)}`}>
                                        {row.nps}
                                        {row.count < LOW_SAMPLE_THRESHOLD && <span className="low-sample-badge" title="Low sample size — interpret with caution"> *</span>}
                                    </td>
                                    <td className="text-right">{row.avg_rating}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div className="location-table-pagination">
                            <span>Showing {tablePage * ROWS_PER_PAGE + 1}-{Math.min((tablePage + 1) * ROWS_PER_PAGE, filteredData.length)} of {filteredData.length}</span>
                            <div>
                                <button disabled={tablePage === 0} onClick={() => setTablePage(p => p - 1)}>Prev</button>
                                {' '}
                                <button disabled={tablePage >= totalPages - 1} onClick={() => setTablePage(p => p + 1)}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </>)}

            {/* Drill-down */}
            {drilldownLoading && <div className="location-loading"><CircularProgress size={24} /></div>}
            {drilldown && !drilldownLoading && (
                <div className="location-drilldown">
                    <div className="location-drilldown-header">
                        <h3>Reviews for: {drilldown.value} ({drilldown.posts.length} posts)</h3>
                        <button className="location-drilldown-close" onClick={() => setDrilldown(null)}>Close</button>
                    </div>
                    <FilteredPostsDisplay
                        posts={drilldown.posts.map(p => ({
                            id: p.post_id,
                            title: p.title,
                            text: p.text,
                            url: p.url,
                            time: p.time,
                            type: p.type,
                            score: p.score,
                            rating: p.rating,
                        }))}
                        isLoading={false}
                    />
                </div>
            )}
        </div>
    );
}
